/* eslint-disable no-undef */
/* eslint-disable no-redeclare */
/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable default-param-last */
import {
  AllowedFilesEntities,
  type CreateFileInput,
  type CreateFileMutationVariables as CreateMutationVariables,
  type DateTime,
  EntityType,
  type FileCategory,
  type File as FileModel,
  type GenerateDocumentInput,
  type GenerateDocumentMutationVariables,
  type LeasePriceHistory,
  type LooseObject,
  type Model,
  type MutationStatus,
  ONE_DAY_IN_SECONDS,
  type OnCreateFileSubscription,
  type OnDeleteFileSubscription,
  type OnUpdateFileSubscription,
  type S3Object,
  calendarDateFromTimestamp,
  cleanInputUpdate,
  createFile as createMutation,
  deleteFile as deleteMutation,
  generateDocument as generateDocumentMutation,
  getDateTimeDifference,
  getFile,
  getFileKey,
  getPublicFileKey,
  getReadId,
  getStoredObjectKeys,
  getTableClientId,
  isNilOrEmpty,
  onCreateFile,
  onDeleteFile,
  onUpdateFile,
  sanitizeFileName,
  syncFiles,
  todayDateTime,
  updateFile as updateMutation,
} from '@rentguru/commons-utils';
import { useQueryClient } from '@tanstack/react-query';
import {
  isTooEarlyToRetryFailedFetch,
  MILLISECONDS_BEFORE_RETRYING_A_FAILED_FETCH,
  NUMBER_OF_MINUTES_FOR_REFETCH,
  deleteAndHideEntityWithFetchBefore,
  get,
  getFilterFieldNameForIndex,
  list,
  mutation,
  removeParametersFromQuery,
  useSubscriptions,
} from '@up2rent/graphql';
import { toast } from '@up2rent/ui-new';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import uniq from 'lodash/uniq';
import React, { type Reducer, useContext, useEffect, useMemo, useReducer } from 'react';
import { useIntl } from 'react-intl';

import { downloadData, fetchStorageObject, getUrl, remove, uploadData } from 'src/auth/storage';
import { syncFilesWithCategoryLabels, syncFiles as syncTenantFiles } from 'src/tenantHooks/graphqlHelper';

import { base64ToBytes } from '../utils/base64Utils';
import { handleNewHEICFile } from '../utils/heicConvertor';

import { FolderDatabaseAction } from './Folderhook/folderContextUtils';
import { useFolderDatabase } from './Folderhook/FolderDatabaseContext';
import { LIVE_FILES_QUERY_KEY } from './useLiveFiles';
import { useUser } from './UserContext';
import { syncFilesForFolders } from './utils/cleanedQueries';

const useFolderDatabaseSafe = () => {
  try {
    return useFolderDatabase();
  } catch (_error) {
    return null;
  }
};

const ENTITY_MODEL_NAME: Model = 'File';

export interface FilesGroupedByCategory {
  category: FileCategory;
  files: S3Object[];
}

export const dataURItoBlob = (dataURI: string) => {
  // convert base64/URLEncoded data component to raw binary data held in a string
  const bytes =
    dataURI.split(',')[0].indexOf('base64') >= 0
      ? base64ToBytes(dataURI.split(',')[1])
      : Uint8Array.from(unescape(dataURI.split(',')[1]), (character) => character.charCodeAt(0));
  // separate out the mime component
  const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
  return new Blob([bytes], { type: mimeString });
};

export const blobToFile = (blob: Blob, fileName: string): File =>
  new File([blob], fileName, {
    type: blob.type,
    lastModified: todayDateTime().epochMilliseconds,
  });

export const getFileBlobFromUrl = async (url: string) => {
  const blobResponse = await fetchStorageObject(url).then((response) => response.blob());
  return blobResponse;
};

export const getArrayBufferFromUrl = async (url: string) => {
  const blobResponse = await fetchStorageObject(url).then((response) => response.arrayBuffer());
  return blobResponse;
};

interface ReplaceFileInput {
  file: File;
  entityType: EntityType;
  entityId: string;
  categoryId: string;
  sharedWithEntityIds?: string[];
}

export interface FilesContext extends Omit<FileState, 'loading'> {
  createFile: (
    file: File,
    entityType: EntityType,
    entityId: string,
    categoryId: string | undefined,
    options?: LooseObject,
    contactType?: string,
    contactId?: string,
    fileKey?: string,
    createDynamoFile?: boolean
  ) => Promise<FileModel | null>;
  updateFile: (original: FileModel, updates: Partial<FileModel>) => Promise<FileModel>;
  deleteFile: (file: FileModel) => Promise<FileModel>;
  replaceFile: (input: ReplaceFileInput) => Promise<FileModel | null>;
  deleteFileAndCleanupOrphanedS3Object: (file: FileModel) => Promise<void>;
  deleteFilesSharingObject: (files: FileModel[]) => Promise<void>;
  deleteFileFromS3: (key: string) => Promise<void>;
  fetchFileFromId: (fileId: string) => Promise<S3Object[]>;
  generateDocument: (input: GenerateDocumentInput) => Promise<MutationStatus>;
  copyFile: (file: FileModel, entityType: EntityType, entityId: string, categoryId: string) => Promise<FileModel>;
  error: string | null;
  fetchFiles: (entityType: EntityType, entityId?: string) => Promise<FileModel[]>;
  fetchFilesByCategoryId: (categoryId: string) => Promise<FileModel[]>;
  getFiles: (entityType: EntityType, entityIds?: string[], category?: FileCategory) => FileModel[];
  fetchFilesByDate: (lastSync?: DateTime) => Promise<FileModel[]>;
  loading: boolean;
}

interface FileState {
  filesCollection: {
    [key: string]: FileModel[];
  };
  loading: {
    [key: string]: boolean;
  };
  subscriptionsLoading: boolean;
  lastSync: {
    [fileId: string]: DateTime;
  };
  lastFetchFailure: {
    [entityType: string]: DateTime;
  };
  shouldFetch: { entityType?: EntityType; entityIds?: string[] }[];
  entityTypes?: EntityType[];
  entityIds?: string[];
  syncedEntityTypes: {
    [entityType: string]: boolean;
  };
}

type Action =
  | {
      type: 'IS_FETCHING';
      payload: { entityType: EntityType };
    }
  | {
      type: 'SHOULD_FETCH';
      payload: { entityType?: EntityType; entityIds?: string[] };
    }
  | {
      type: 'SET_SUBSCRIPTION_LOADING';
      payload: { subscriptionLoading: boolean };
    }
  | {
      type: 'ADD_FILE' | 'UPDATE_FILE';
      payload: { file: FileModel };
    }
  | {
      type: 'FETCHED';
      payload: { files: FileModel[]; entityType: EntityType; keyIdsFetched: string[] };
    }
  | {
      type: 'ERROR';
      payload: { error: string; entityType: EntityType };
    }
  | {
      type: 'DELETE_FILE';
      payload: { id: string; entityType: EntityType };
    }
  | {
      type: 'FAILURE_BACKOFF_ELAPSED';
    };

const initialState: FileState = {
  filesCollection: {},
  loading: {},
  shouldFetch: [],
  lastSync: {},
  lastFetchFailure: {},
  subscriptionsLoading: false,
  syncedEntityTypes: {},
};

const fileReducer = (state: FileState, action: Action): FileState => {
  switch (action.type) {
    case 'SHOULD_FETCH':
      const currentEntityType = action.payload.entityType;
      const currentEntityIds = action.payload.entityIds ?? [];
      if (!isEmpty(currentEntityIds)) {
        const existingShouldFetchObject = state.shouldFetch.find(
          (shouldFetchObject) => shouldFetchObject.entityType === currentEntityType
        );
        const existingEntityIds = existingShouldFetchObject?.entityIds ?? [];
        const allIdsOfEntityTypeAlreadyInFetch = isEmpty(existingEntityIds) && existingShouldFetchObject;

        const idsToFetch = allIdsOfEntityTypeAlreadyInFetch
          ? []
          : currentEntityIds.filter((entityId) => {
              const lastSyncForThisId = state.lastSync[entityId];
              const hasNotBeenFetchRecently = !(
                lastSyncForThisId &&
                getDateTimeDifference({ start: lastSyncForThisId, end: todayDateTime() }, 'minutes') <
                  NUMBER_OF_MINUTES_FOR_REFETCH
              );
              const alreadyToFetch = !existingEntityIds.includes(entityId);
              return hasNotBeenFetchRecently && alreadyToFetch;
            });

        const isInFailureBackoff =
          !isNil(currentEntityType) && isTooEarlyToRetryFailedFetch(state.lastFetchFailure[currentEntityType]);
        if (state.loading[currentEntityType!] || isEmpty(idsToFetch) || isInFailureBackoff) {
          return state;
        }

        const newEntityIds = uniq([...idsToFetch, ...existingEntityIds]);
        const newShouldFetch = { entityType: currentEntityType, entityIds: newEntityIds };

        return {
          ...state,
          shouldFetch: [
            ...state.shouldFetch.filter((shouldFetchObject) => shouldFetchObject.entityType !== currentEntityType),
            newShouldFetch,
          ],
        };
      }

      if (
        state.syncedEntityTypes[currentEntityType!] ||
        !isNil(state.shouldFetch.find((value) => value.entityType === currentEntityType))
      ) {
        return state;
      }
      return {
        ...state,
        shouldFetch: [...state.shouldFetch, { entityType: currentEntityType }],
      };
    case 'IS_FETCHING':
      if (state.loading[action.payload.entityType]) {
        return state;
      }
      return {
        ...state,
        loading: { ...state.loading, [action.payload.entityType]: true },
      };
    case 'FETCHED':
      const files = action.payload.files;

      const fileIds = files.map((file) => file.id);
      const foreignKeysFetched = action.payload.keyIdsFetched;

      const entityType = action.payload.entityType;
      const shouldFetchUpdated = state.shouldFetch;
      shouldFetchUpdated.shift();

      const newLastSync = { ...state.lastSync };
      foreignKeysFetched.forEach((key) => (newLastSync[key] = todayDateTime()));

      return {
        ...state,
        filesCollection: {
          ...state.filesCollection,
          [entityType]: [
            ...(state.filesCollection[entityType] ?? []).filter((file) => !fileIds.includes(file.id)),
            ...files,
          ],
        },
        loading: { ...state.loading, [action.payload.entityType]: false },
        shouldFetch: shouldFetchUpdated,
        lastSync: newLastSync,
        syncedEntityTypes: isNilOrEmpty(foreignKeysFetched)
          ? { ...state.syncedEntityTypes, [entityType]: true }
          : state.syncedEntityTypes,
      };
    case 'ERROR':
      return {
        ...state,
        loading: { ...state.loading, [action.payload.entityType]: false },
        lastFetchFailure: { ...state.lastFetchFailure, [action.payload.entityType]: todayDateTime() },
        shouldFetch: state.shouldFetch.filter(
          (shouldFetchObject) => shouldFetchObject.entityType !== action.payload.entityType
        ),
      };
    case 'FAILURE_BACKOFF_ELAPSED':
      // Clearing the elapsed failures re-renders the consumers, whose getFiles calls enqueue them again.
      return {
        ...state,
        lastFetchFailure: Object.fromEntries(
          Object.entries(state.lastFetchFailure).filter(([, lastFailure]) => isTooEarlyToRetryFailedFetch(lastFailure))
        ),
      };
    case 'ADD_FILE':
      const newFile = action.payload.file;
      const newFileCollection = (state.filesCollection[newFile.foreignTableName] = [
        ...(state.filesCollection[newFile.foreignTableName] ?? []).filter((oldFile) => oldFile.id !== newFile.id),
        newFile,
      ]);

      return { ...state, filesCollection: { ...state.filesCollection, [newFile.foreignTableName]: newFileCollection } };
    case 'UPDATE_FILE':
      if (!state.filesCollection) {
        return state;
      }
      const updatedFile = action.payload.file;
      const oldFiles = state.filesCollection[updatedFile.foreignTableName] ?? [];

      state.filesCollection[updatedFile.foreignTableName] = [
        ...oldFiles.filter((oldFile) => oldFile.id !== updatedFile.id),
        updatedFile,
      ];

      return { ...state };
    case 'DELETE_FILE':
      if (!state.filesCollection) {
        return state;
      }
      const entityTypeFromAction = action.payload.entityType;
      if (entityTypeFromAction) {
        state.filesCollection[entityTypeFromAction] = (state.filesCollection[entityTypeFromAction] ?? []).filter(
          (file) => file.id !== action.payload.id
        );
      }
      return { ...state };
    case 'SET_SUBSCRIPTION_LOADING':
      const newSubscriptionLoading = action.payload.subscriptionLoading;
      return { ...state, subscriptionsLoading: newSubscriptionLoading };
    default:
      return state;
  }
};

const getSyncQuery = (isTenant: boolean = false, isOwner: boolean = false) => {
  if (isTenant) {
    return syncTenantFiles;
  }
  if (isOwner) {
    return removeParametersFromQuery(syncFilesWithCategoryLabels, 'category');
  }
  return syncFilesWithCategoryLabels;
};

const fetchFilesWithFilters = async (
  clientId?: string,
  entityType?: EntityType | EntityType[],
  entityId?: string,
  isTenant: boolean = false,
  isOwner: boolean = false
) => {
  const syncQuery = getSyncQuery(isTenant, isOwner);

  if (!isNil(entityType) && !isNil(entityId) && !isArray(entityType)) {
    return await list<FileModel>(syncQuery, getFilterFieldNameForIndex('byForeignKey'), entityId, {
      foreignTableName: { eq: entityType },
    });
  }
  if (!isNil(entityType) && !isArray(entityType) && !isNil(clientId)) {
    return await list<FileModel>(
      syncQuery,
      getFilterFieldNameForIndex('byClientId'),
      getTableClientId(clientId, ENTITY_MODEL_NAME),
      {
        foreignTableName: { eq: entityType },
      }
    );
  }
  if (!isNil(entityType) && isArray(entityType) && !isNil(clientId)) {
    return await list<FileModel>(
      syncQuery,
      getFilterFieldNameForIndex('byClientId'),
      getTableClientId(clientId, ENTITY_MODEL_NAME),
      {
        or: [
          {
            foreignTableName: { eq: entityType[0] },
          },
          {
            foreignTableName: { eq: entityType[1] },
          },
        ],
      }
    );
  }
  if (!isNil(entityId)) {
    return await list<FileModel>(syncQuery, getFilterFieldNameForIndex('byForeignKey'), entityId);
  }
  if (!isNil(clientId)) {
    return await list<FileModel>(
      syncQuery,
      getFilterFieldNameForIndex('byClientId'),
      getTableClientId(clientId, ENTITY_MODEL_NAME)
    );
  }

  return null;
};

const processFile = async (file: FileModel, metadata: boolean): Promise<S3Object> => {
  const fileKey = getPublicFileKey(file.key);
  const fileName = file.key.substring(file.key.indexOf('_') + 1);
  const url = await getUrl({
    key: fileKey,
    options: { expiresIn: ONE_DAY_IN_SECONDS },
  });
  let obj: S3Object = { ...file, fileName, url: url.url.toString() };
  if (metadata) {
    const meta = await downloadData({ key: fileKey }).result;
    obj = {
      ...obj,
      lastModified: isNil(meta.lastModified) ? undefined : calendarDateFromTimestamp(meta.lastModified.toISOString()),
      metadata: meta.metadata,
    };
  }
  return obj;
};

export const getS3ObjectUrls = (
  files: FileModel[] | undefined | null,
  metadata: boolean | undefined = false
): Promise<S3Object[]> => Promise.all((files ?? []).map((file) => processFile(file, metadata)));

export const fetchFilesAndGetS3ObjectUrls = async (
  entityType?: EntityType | EntityType[],
  entityId?: string
): Promise<S3Object[]> => {
  const files = await fetchFilesWithFilters(undefined, entityType, entityId);
  return await getS3ObjectUrls(files);
};

export const fetchFilesOfLeasePriceHistory = async (
  leasePriceHistory: LeasePriceHistory | undefined
): Promise<FileModel[]> => {
  if (isNil(leasePriceHistory)) return [];
  const leaseFiles = await list<FileModel>(syncFiles, 'foreignKey', leasePriceHistory!.id);
  return leaseFiles;
};

export const FilesContext = React.createContext<FilesContext | null>(null);

export const FilesContextProvider: React.FC<{
  children?: React.ReactNode;
}> = ({ children }) => {
  const [state, dispatch] = useReducer<Reducer<FileState, Action>>(fileReducer, initialState);
  const { clientId, isOwner, isTenant, userId } = useUser();
  const queryClient = useQueryClient();
  const { formatMessage } = useIntl();

  useEffect(() => {
    if (isEmpty(state.lastFetchFailure)) {
      return undefined;
    }
    const retryTimeout = setTimeout(
      () => dispatch({ type: 'FAILURE_BACKOFF_ELAPSED' }),
      MILLISECONDS_BEFORE_RETRYING_A_FAILED_FETCH
    );
    return () => clearTimeout(retryTimeout);
  }, [state.lastFetchFailure]);

  const folderDatabase = useFolderDatabaseSafe();
  const addFileToSync = !isTenant && folderDatabase?.addFolderToSync ? folderDatabase.addFolderToSync : async () => {};

  useSubscriptions<OnCreateFileSubscription, OnUpdateFileSubscription, OnDeleteFileSubscription>(
    onCreateFile,
    onUpdateFile,
    onDeleteFile,
    async (data) => {
      const file = data.onCreateFile as FileModel;
      dispatch({ type: 'ADD_FILE', payload: { file } });
      await addFileToSync(FolderDatabaseAction.CREATE, file);
      // The following line and its corresponding useEffect exist because we want to change the loading status to
      // enable components to receive the last file. It is not possible by doing IS_FETCHING and then FETCHED because it
      // will be executed too quickly and the loading will stay at false.
      dispatch({ type: 'SET_SUBSCRIPTION_LOADING', payload: { subscriptionLoading: true } });
    },
    async (data) => {
      const file = data.onUpdateFile as FileModel;
      dispatch({ type: 'UPDATE_FILE', payload: { file } });
      await addFileToSync(FolderDatabaseAction.UPDATE, file);
      // The following line and its corresponding useEffect exist because we want to change the loading status to
      // enable components to receive the last file. It is not possible by doing IS_FETCHING and then FETCHED because it
      // will be executed too quickly and the loading will stay at false.
      dispatch({ type: 'SET_SUBSCRIPTION_LOADING', payload: { subscriptionLoading: true } });
    },
    async (data) => {
      const file = data.onDeleteFile as FileModel;
      dispatch({ type: 'DELETE_FILE', payload: { id: file.id, entityType: file.foreignTableName as EntityType } });
      await addFileToSync(FolderDatabaseAction.DELETE, file);
      // The following line and its corresponding useEffect exist because we want to change the loading status to
      // enable components to receive the last file. It is not possible by doing IS_FETCHING and then FETCHED because it
      // will be executed too quickly and the loading will stay at false.
      dispatch({ type: 'SET_SUBSCRIPTION_LOADING', payload: { subscriptionLoading: true } });
    }
  );

  useEffect(() => {
    let unmounted = false;
    const fetch = async () => {
      const currentFetchObject = state.shouldFetch[0];
      const entityIds = currentFetchObject.entityIds ?? [];
      const entityType = currentFetchObject.entityType!;
      if (state.loading[entityType]) return;
      try {
        let fetchedFiles;
        if (entityIds?.length > 1) {
          fetchedFiles = await fetchFilesByForeignKeys(entityIds, entityType as EntityType);
        } else {
          const entityId = entityIds?.[0];
          fetchedFiles = await fetchFiles(entityType, entityId);
        }
        if (!unmounted) {
          dispatch({
            type: 'FETCHED',
            payload: { files: fetchedFiles, entityType: entityType!, keyIdsFetched: entityIds },
          });
        }
      } catch (error) {
        console.error('[files] fetching files failed', error);
      }
    };
    if (!isEmpty(state.shouldFetch)) {
      fetch();
    }
    return () => {
      unmounted = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.shouldFetch, state.loading]);

  useEffect(() => {
    if (state.subscriptionsLoading) {
      dispatch({ type: 'SET_SUBSCRIPTION_LOADING', payload: { subscriptionLoading: false } });
    }
  }, [state.subscriptionsLoading]);

  const createFile = async (
    file: File,
    entityType: EntityType,
    entityId: string,
    categoryId: string | undefined,
    options: LooseObject = {},
    contactType?: string,
    contactId?: string,
    existingFileKey?: string,
    createDynamoFile = true
  ): Promise<FileModel | null> => {
    if (createDynamoFile && isNil(categoryId)) {
      console.error(`Cannot upload ${file.name}: no readable document category for ${entityType}`);
      return null;
    }
    const readers = isOwner || isTenant ? [userId!] : undefined;
    let fileToCreate = new File([file], sanitizeFileName(file.name), {
      type: file.type,
      lastModified: file.lastModified,
    });
    if (fileToCreate.type === 'image/heic') {
      const convertedFileBlob = await handleNewHEICFile(file, clientId);
      if (!convertedFileBlob) {
        return null;
      }
      const fileName = fileToCreate.name.replace('.heic', '.jpg');
      fileToCreate = new File([convertedFileBlob], fileName, {
        type: 'image/jpeg',
        lastModified: todayDateTime().epochMilliseconds,
      });
    }
    try {
      const fileKey =
        existingFileKey ?? getFileKey(clientId!, entityType, fileToCreate.name, false, contactType, contactId);

      if (!existingFileKey) {
        const fileKeyCleaned = getPublicFileKey(fileKey);
        await uploadData({
          key: fileKeyCleaned,
          data: fileToCreate,
          options: {
            contentDisposition: `attachment; filename="${fileToCreate.name}"`,
            ...options,
          },
        }).result;
      }

      if (!createDynamoFile || isNil(categoryId)) {
        return { key: fileKey } as FileModel;
      }

      const newFile = await mutation<FileModel, CreateMutationVariables>(createMutation, {
        input: {
          bucket: import.meta.env.VITE_FILES_BUCKET,
          region: import.meta.env.VITE_FILES_REGION,
          mimeType: fileToCreate.type,
          size: fileToCreate.size,
          clientId: getTableClientId(clientId!, ENTITY_MODEL_NAME),
          readId: getReadId(clientId!, ENTITY_MODEL_NAME),
          key: fileKey,
          categoryId: categoryId,
          foreignKey: entityId,
          foreignTableName: entityType,
          ...(!isNil(readers) && { readers }),
        },
      });
      dispatch({ type: 'ADD_FILE', payload: { file: newFile } });
      await addFileToSync(FolderDatabaseAction.CREATE, newFile);
      return newFile;
    } catch (err) {
      console.error('Error creating file', err);
      return null;
    }
  };

  const copyFile = async (
    fileModelToCopy: FileModel,
    entityType: EntityType,
    entityId: string,
    categoryId: string
  ): Promise<FileModel> => {
    // Keep the same key so the S3 object is shared (no re-upload), only the File record is new.
    const newFileInput: CreateFileInput = {
      bucket: fileModelToCopy.bucket,
      region: fileModelToCopy.region,
      mimeType: fileModelToCopy.mimeType,
      size: fileModelToCopy.size,
      clientId: fileModelToCopy.clientId,
      readId: fileModelToCopy.readId,
      key: fileModelToCopy.key,
      categoryId,
      foreignKey: entityId,
      foreignTableName: entityType,
      ...(!isNil(fileModelToCopy.readers) && { readers: fileModelToCopy.readers }),
    };
    const newFile = await mutation<FileModel, CreateMutationVariables>(createMutation, {
      input: newFileInput,
    });
    dispatch({ type: 'ADD_FILE', payload: { file: newFile } });
    await addFileToSync(FolderDatabaseAction.CREATE, newFile);

    return newFile;
  };

  const updateFile = async (original: FileModel, updates: Partial<FileModel>): Promise<FileModel> => {
    const result = await mutation<FileModel>(updateMutation, {
      input: { id: original.id, _version: original._version, ...cleanInputUpdate(updates, false) },
    });
    dispatch({ type: 'UPDATE_FILE', payload: { file: result } });
    return result;
  };

  const removeStoredObject = async (file: Pick<FileModel, 'key' | 'mimeType'>): Promise<void> => {
    await Promise.all(getStoredObjectKeys(file).map((storedKey) => remove({ key: getPublicFileKey(storedKey) })));
  };

  const deleteFile = async (fileToDelete: FileModel): Promise<FileModel> => {
    const filesSharingKey = await list<FileModel>(syncFiles, 'key', fileToDelete.key);
    const isKeyLookupTrustworthy = filesSharingKey.some((file) => file.id === fileToDelete.id);
    const otherFilesSharingKey = filesSharingKey.filter((file) => file.id !== fileToDelete.id);

    const result = await deleteAndHideEntityWithFetchBefore<FileModel>(fileToDelete, getFile, deleteMutation);
    dispatch({
      type: 'DELETE_FILE',
      payload: { id: fileToDelete.id, entityType: fileToDelete.foreignTableName as EntityType },
    });
    await queryClient.invalidateQueries({ queryKey: [LIVE_FILES_QUERY_KEY] });
    await addFileToSync(FolderDatabaseAction.DELETE, fileToDelete);

    if (isKeyLookupTrustworthy && isEmpty(otherFilesSharingKey)) {
      await removeStoredObject(fileToDelete);
    }

    return result;
  };

  const deleteFilesSharingObject = async (filesToDelete: FileModel[]) => {
    for (const fileToDelete of filesToDelete) {
      // Sequential on purpose: deleteFile removes the S3 object only once no other record shares its key.
      await deleteFile(fileToDelete);
    }
  };

  const replaceFile = async ({
    file,
    entityType,
    entityId,
    categoryId,
    sharedWithEntityIds = [],
  }: ReplaceFileInput): Promise<FileModel | null> => {
    try {
      const entityFiles =
        (await fetchFilesWithFilters(clientId ?? undefined, entityType, entityId, isTenant, isOwner)) ?? [];
      const previousFiles = entityFiles.filter((entityFile) => entityFile.categoryId === categoryId);
      const createdFile = await createFile(file, entityType, entityId, categoryId);
      if (isNil(createdFile)) {
        toast.error(formatMessage({ id: 'error.replaceFile' }));
        return null;
      }
      await Promise.all(
        sharedWithEntityIds.map((sharedEntityId) => copyFile(createdFile, entityType, sharedEntityId, categoryId))
      );
      for (const previousFile of previousFiles) {
        const filesSharingKey = await list<FileModel>(syncFiles, 'key', previousFile.key);
        const previousCopies = filesSharingKey.filter(
          (existingFile) => existingFile.foreignTableName === entityType && existingFile.id !== previousFile.id
        );
        await deleteFilesSharingObject([previousFile, ...previousCopies]);
      }
      return createdFile;
    } catch (error) {
      console.error('[files] replacing the file failed', error);
      toast.error(formatMessage({ id: 'error.replaceFile' }));
      return null;
    }
  };

  const deleteFileAndCleanupOrphanedS3Object = async (fileToDelete: FileModel): Promise<void> => {
    await deleteAndHideEntityWithFetchBefore<FileModel>(fileToDelete, getFile, deleteMutation);
    dispatch({
      type: 'DELETE_FILE',
      payload: { id: fileToDelete.id, entityType: fileToDelete.foreignTableName as EntityType },
    });
    await queryClient.invalidateQueries({ queryKey: [LIVE_FILES_QUERY_KEY] });
    await addFileToSync(FolderDatabaseAction.DELETE, fileToDelete);

    let filesSharingKey: FileModel[];
    try {
      filesSharingKey = await list<FileModel>(syncFilesForFolders, undefined, undefined, {
        key: { eq: fileToDelete.key },
      });
    } catch (error) {
      console.error('[files] checking whether the deleted file S3 object is still referenced failed', error);
      return;
    }
    const s3ObjectStillReferenced = filesSharingKey.some((file) => file.id !== fileToDelete.id);
    if (!s3ObjectStillReferenced) {
      await removeStoredObject(fileToDelete);
    }
  };

  const deleteFileFromS3 = async (key: string) => {
    await remove({ key: getPublicFileKey(key) });
  };

  const fetchFiles = async (entityType: EntityType, entityId?: string): Promise<FileModel[]> => {
    dispatch({ type: 'IS_FETCHING', payload: { entityType } });
    try {
      const fetchedFiles = (await fetchFilesWithFilters(clientId!, entityType, entityId, isTenant, isOwner)) ?? [];
      const entityIds = entityId ? [entityId] : [];
      dispatch({
        type: 'FETCHED',
        payload: { files: fetchedFiles, entityType: entityType!, keyIdsFetched: entityIds },
      });
      return fetchedFiles;
    } catch (error) {
      console.error('[files] fetching files failed', error);
      dispatch({
        type: 'ERROR',
        payload: { error: error instanceof Error ? error.message : String(error), entityType },
      });
      throw error;
    }
  };

  const getFiles = (entityType: EntityType, entityIds?: string[], category?: FileCategory) => {
    dispatch({ type: 'SHOULD_FETCH', payload: { entityType, entityIds } });
    if (state.loading[entityType]) {
      return [];
    }

    let selectedFiles = state.filesCollection[entityType] ?? [];
    if (category) {
      selectedFiles = selectedFiles.filter((file) => file.category?.fileCategory === category);
    }
    if (entityIds) {
      return selectedFiles.filter((file) => entityIds.includes(file.foreignKey));
    }
    return selectedFiles;
  };

  const fetchFilesByCategoryId = async (categoryId: string) =>
    await list<FileModel>(syncFilesWithCategoryLabels, getFilterFieldNameForIndex('byCategory'), categoryId);

  const fetchFileFromId = async (fileId: string) => {
    const file = await get<FileModel>(getFile, fileId);
    if (isNil(file)) {
      return [];
    }
    return await getS3ObjectUrls([file]);
  };

  const generateDocument = async (input: GenerateDocumentInput): Promise<MutationStatus> => {
    const result = await mutation<MutationStatus, GenerateDocumentMutationVariables>(generateDocumentMutation, {
      input,
    });
    return result;
  };

  const fetchFilesByForeignKeys = async (foreignKeys: string[], foreignTableName: string | EntityType) => {
    const allIdsFilter = foreignKeys.map((key) => ({ foreignKey: { eq: key } }));
    if (isEmpty(allIdsFilter)) {
      return [];
    }
    dispatch({ type: 'IS_FETCHING', payload: { entityType: foreignTableName as EntityType } });
    try {
      const filesPromises = foreignKeys.map((foreignKey) =>
        list<FileModel>(
          isTenant ? syncTenantFiles : syncFilesWithCategoryLabels,
          getFilterFieldNameForIndex('byClientId'),
          getTableClientId(clientId!, ENTITY_MODEL_NAME),
          {
            and: [{ foreignTableName: { eq: foreignTableName } }, { foreignKey: { eq: foreignKey } }],
          }
        )
      );

      const fetchedFiles = (await Promise.all(filesPromises)).flat();

      dispatch({
        type: 'FETCHED',
        payload: {
          files: fetchedFiles,
          entityType: foreignTableName as EntityType,
          keyIdsFetched: foreignKeys,
        },
      });

      return fetchedFiles;
    } catch (error) {
      console.error('[files] fetching files by foreign keys failed', error);
      dispatch({
        type: 'ERROR',
        payload: {
          error: error instanceof Error ? error.message : String(error),
          entityType: foreignTableName as EntityType,
        },
      });
      throw error;
    }
  };

  const fetchFilesByDate = async (lastSync?: DateTime): Promise<FileModel[]> => {
    const files = await list<FileModel>(
      syncFilesForFolders,
      getFilterFieldNameForIndex('byClientId'),
      getTableClientId(clientId!, ENTITY_MODEL_NAME),
      undefined,
      lastSync,
      1000,
      Boolean(lastSync)
    );
    return files.filter((file) => file._deleted || AllowedFilesEntities.includes(file.foreignTableName as EntityType));
  };

  const getLoadingFromState = () =>
    Object.values(state.loading).reduce((acc, current) => acc || current, false) || state.subscriptionsLoading;

  const values = useMemo(
    () => ({
      ...state,
      loading: getLoadingFromState(),
      error: null,
      createFile,
      deleteFileFromS3,
      updateFile,
      deleteFile,
      replaceFile,
      deleteFileAndCleanupOrphanedS3Object,
      deleteFilesSharingObject,
      fetchFileFromId,
      generateDocument,
      copyFile,
      fetchFiles,
      fetchFilesByCategoryId,
      getFiles,
      fetchFilesByDate,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state]
  );

  return <FilesContext.Provider value={values}>{children}</FilesContext.Provider>;
};

export const useFiles = (): FilesContext => {
  const context = useContext<FilesContext | null>(FilesContext);

  if (isNil(context)) {
    throw new Error('`useFiles` hook must be used within a `FilesContextProvider` component');
  }

  return context as FilesContext;
};
